import { supabase } from '../../providers/supabase/client'

// ── Room Types ────────────────────────────────────────────────

export const fetchRoomTypes = async () => {
  return await supabase
    .from('room_types')
    .select('*')
    .order('name', { ascending: true })
}

export const createRoomType = async (name: string, description?: string) => {
  return await supabase
    .from('room_types')
    .insert([{ name, description }])
    .select()
}

// ── Hotel Configurations ──────────────────────────────────────

export const fetchHotelConfigurations = async (hotelId?: number) => {
  let query = supabase
    .from('hotel_configurations')
    .select(`
      *,
      room_types(*),
      hotels(id, name, stars)
    `)
    .eq('is_active', true)
    .order('created_at', { ascending: true })

  if (hotelId) {
    query = query.eq('hotel_id', hotelId)
  }

  return await query
}

export const fetchConfigurationsByHotelIds = async (hotelIds: number[]) => {
  return await supabase
    .from('hotel_configurations')
    .select(`
      *,
      room_types(*),
      hotels(id, name, stars)
    `)
    .in('hotel_id', hotelIds)
    .eq('is_active', true)
    .order('hotel_id', { ascending: true })
}

export const createHotelConfiguration = async (config: {
  hotel_id: number,
  room_type_id: string,
  is_non_refundable: boolean,
  meal_plan?: string,
  notes?: string,
  custom_name?: string | null,
  room_image_urls?: string[],
  additional_info?: any
}) => {
  const mealPlan = config.meal_plan || 'Sin especificar'

  // Check if a configuration already exists for this (hotel_id, room_type_id, is_non_refundable, meal_plan)
  const { data: existing } = await supabase
    .from('hotel_configurations')
    .select('id, is_active')
    .eq('hotel_id', config.hotel_id)
    .eq('room_type_id', config.room_type_id)
    .eq('is_non_refundable', config.is_non_refundable)
    .eq('meal_plan', mealPlan)
    .maybeSingle()

  if (existing) {
    return await supabase
      .from('hotel_configurations')
      .update({
        ...config,
        meal_plan: mealPlan,
        is_active: true
      })
      .eq('id', existing.id)
      .select(`
        *,
        room_types(*),
        hotels(id, name, stars)
      `)
  }

  return await supabase
    .from('hotel_configurations')
    .insert([{ ...config, meal_plan: mealPlan }])
    .select(`
      *,
      room_types(*),
      hotels(id, name, stars)
    `)
}

export const updateHotelConfiguration = async (id: string, data: {
  room_type_id?: string,
  is_non_refundable?: boolean,
  meal_plan?: string,
  notes?: string,
  custom_name?: string | null,
  room_image_urls?: string[],
  additional_info?: any,
  is_active?: boolean
}) => {
  return await supabase
    .from('hotel_configurations')
    .update(data)
    .eq('id', id)
    .select(`
      *,
      room_types(*),
      hotels(id, name, stars)
    `)
}

export const deleteHotelConfiguration = async (id: string) => {
  // Soft delete by setting is_active = false
  return await supabase
    .from('hotel_configurations')
    .update({ is_active: false })
    .eq('id', id)
}

// ── Room Images Storage ───────────────────────────────────────

const ROOM_BUCKET = 'room-images'

/**
 * Upload a room configuration image to a deterministic slot.
 * Slot examples: 'room_0', 'room_1', 'room_2'
 */
export const uploadRoomImage = async (
  configId: string,
  file: File,
  slot: string,
): Promise<string | null> => {
  const path = `${configId}/${slot}`

  const { error } = await supabase.storage
    .from(ROOM_BUCKET)
    .upload(path, file, {
      upsert: true,
      contentType: file.type,
      cacheControl: '0',
    })

  if (error) {
    console.error(`Error uploading room image [${slot}]:`, error)
    return null
  }

  const { data: urlData } = supabase.storage
    .from(ROOM_BUCKET)
    .getPublicUrl(path)

  return urlData?.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : null
}

/**
 * Delete a specific room image from storage by slot.
 */
export const deleteRoomImage = async (
  configId: string,
  slot: string,
): Promise<boolean> => {
  const path = `${configId}/${slot}`

  const { error } = await supabase.storage
    .from(ROOM_BUCKET)
    .remove([path])

  if (error) {
    console.error(`Error deleting room image [${slot}]:`, error)
    return false
  }
  return true
}
